import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { UserProvider, useUser } from "@/contexts/UserContext";
import { ensureServiceWorker, isPushSupported, registerInstallListener } from "@/lib/push";

// Pages
import WelcomePage from "@/pages/WelcomePage";
import CompleteSignupPage from "@/pages/CompleteSignupPage";
import LandingPage from "@/pages/LandingPage";
import RoleSelectPage from "@/pages/RoleSelectPage";
import MainHub from "@/pages/MainHub";
import ClientDashboard from "@/pages/ClientDashboard";
import SessionPage from "@/pages/SessionPage";
import ProgressPage from "@/pages/ProgressPage";
import ExcavationWriting from "@/pages/ExcavationWriting";
import VoiceIntegration from "@/pages/VoiceIntegration";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import AuthCallback from "@/pages/AuthCallback";
import CoachDashboard from "@/pages/CoachDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import OperationListenMasterView from "@/pages/OperationListenMasterView";
import TeachersCorner from "@/pages/TeachersCorner";
import StudentsLounge from "@/pages/StudentsLounge";
import SettingsPage from "@/pages/SettingsPage";
import OnboardingPage from "@/pages/OnboardingPage";
import HelpPage from "@/pages/HelpPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import FlowGrid from "@/components/FlowGrid";
import OperationListen from "@/components/OperationListen";

const OperationListenRoute = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get('mode') === 'facilitator' ? 'facilitator' : 'lobby';
  return <OperationListen onExit={() => navigate('/client/hub')} initialMode={mode} />;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-mist flex items-center justify-center">
        <div className="text-obsidian-night text-xl font-body">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role if specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'master_admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.role === 'coach') {
      return <Navigate to="/coach/dashboard" replace />;
    } else {
      return <Navigate to="/client/hub" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  // Register the service worker once at boot so Push notifications work
  // as soon as the user grants permission. Runs on all routes because the
  // subscription flow needs the SW active for /api/push/subscribe.
  useEffect(() => {
    if (isPushSupported()) {
      ensureServiceWorker();
    }
    registerInstallListener();
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<WelcomePage />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/role-select" element={<RoleSelectPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/complete" element={<CompleteSignupPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/landing" element={<LandingPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operation-listen"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <OperationListenMasterView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/teachers-corner"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <TeachersCorner />
          </ProtectedRoute>
        }
      />

      {/* Coach Routes */}
      <Route
        path="/coach/dashboard"
        element={
          <ProtectedRoute allowedRoles={['coach', 'master_admin']}>
            <CoachDashboard />
          </ProtectedRoute>
        }
      />

      {/* Client Routes */}
      <Route
        path="/client/hub"
        element={
          <ProtectedRoute>
            <MainHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/flow-grid"
        element={
          <ProtectedRoute>
            <FlowGrid />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/students-lounge"
        element={<StudentsLounge />}
      />
      <Route
        path="/students-lounge"
        element={<StudentsLounge />}
      />
      <Route
        path="/client/session"
        element={
          <ProtectedRoute>
            <SessionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/progress"
        element={
          <ProtectedRoute>
            <ProgressPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/excavation"
        element={
          <ProtectedRoute>
            <ExcavationWriting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/voice"
        element={
          <ProtectedRoute>
            <VoiceIntegration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/operation-listen"
        element={
          <ProtectedRoute>
            <OperationListenRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Legacy route redirect */}
      <Route path="/dashboard" element={<Navigate to="/client/hub" replace />} />

      {/* Fallback - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <UserProvider>
      <div className="App">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </div>
    </UserProvider>
  );
}

export default App;
